import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { FormEventHandler, FunctionComponent, PropsWithChildren } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Footer } from "../Footer";
import { useSetExpandManualLocationAtom } from "../location/expandManualLocationAtom";
import { useOnboardingPageContextAtomValue } from "../onboardingPageContextAtom";
import { rateMapper } from "../pricing/constants";
import { isOnboardLocationPath, isOnboardProfilePath, isOnboardPublishPath } from "../routes/onboardRoutePaths";
import { useSidebarAtomValue } from "../sidebar/sidebarAtom";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { useFooterNavigation } from "../useFooterNavigation";
import { useOnboardingRouteId } from "../useOnboardingRouteId";
import { formInvalidHandler } from "./formInvalidHandler";
import { useDisableFieldMetadata } from "./useDisableFieldMetadata";
import { useFormHandler } from "./useFormHandler";

interface Props extends PropsWithChildren {
    data: PropertyData;
}

export const PropertyForm: FunctionComponent<Props> = ({ data: propertyData, children }) => {
    const {
        cmsData: {
            common: { back, next },
            publish: { publish },
        },
    } = useStaticDataAtomValue();
    const { disablePublishing } = useOnboardingPageContextAtomValue();
    const sidebarAtomValue = useSidebarAtomValue();
    const { pathname } = useDynamicLocation();
    const route = useOnboardingRouteId();
    const form = useForm<PropertyData>({
        values: propertyData,
        resetOptions: { keepDirtyValues: true },
        shouldFocusError: true,
    });
    const contractId = useWatch({
        control: form.control,
        name: "contractId",
    });
    const { mutate, isLoading } = useFormHandler();
    const setExpandManualLocationAtom = useSetExpandManualLocationAtom();
    const { handleBack } = useFooterNavigation();
    const disableFieldMetadata = useDisableFieldMetadata();

    const isProfilePage = isOnboardProfilePath(pathname);
    const isPublishPage = isOnboardPublishPath(pathname);

    const handleSubmit = (data: PropertyData) => {
        const isChannelManager = data.rate === rateMapper.ChannelManager;

        if (!isProfilePage)
            return mutate({
                ...data,
                nightlyRate: typeof data.nightlyRate === "string" ? Number(data.nightlyRate) : data.nightlyRate,
                weeklyDiscount: isChannelManager ? null : data.weeklyDiscount,
                monthlyDiscount: isChannelManager ? null : data.monthlyDiscount,
                firstAllotment: isChannelManager ? null : data.firstAllotment,
                firstAllotmentDate: isChannelManager ? null : data.firstAllotmentDate,
            });
    };

    const formSubmitEvent: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();

        const formSubmitHandler = form.handleSubmit(handleSubmit, formInvalidHandler("property", route.id, pathname));

        // if user landed on location page, expand fields first then call react-hook-form
        if (isOnboardLocationPath(pathname)) {
            setExpandManualLocationAtom(true);
            setTimeout(() => {
                return formSubmitHandler();
            }, 1);
        } else {
            return formSubmitHandler();
        }
    };

    return (
        <FormProvider {...form}>
            <form id="property-data-form" onSubmit={formSubmitEvent}>
                {children}
                {!isProfilePage && (
                    <Footer
                        secondary={{
                            type: "button",
                            label: back,
                            disabled: isLoading || sidebarAtomValue.length === 0,
                            dataElementName: "list-your-property-back",
                        }}
                        primary={{
                            type: "submit",
                            label: isPublishPage ? publish : next,
                            disabled:
                                isLoading ||
                                disableFieldMetadata ||
                                (isPublishPage &&
                                    (disablePublishing || contractId === null || contractId.length === 0)),
                            loading: isLoading,
                            dataElementName: "list-your-property-next",
                        }}
                        onSecondary={handleBack}
                    />
                )}
            </form>
        </FormProvider>
    );
};
